import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getWedding } from "../api/weddings";
import { getRequests } from "../api/requests";
import { Wedding } from "../types";
import { formatDate } from "../utils/format";
import { useAuth } from "../context/AuthContext";
import Loading from "../components/Loading";
import ErrorMessage from "../components/ErrorMessage";
import GuestListManager from "../components/GuestListManager";
import RequestList from "../components/RequestList";
import TierSetup from "../components/TierSetup";
import StripeConnectPanel from "../components/StripeConnectPanel";

type ManageTab = "tiers" | "guests" | "requests" | "stripe";

const TAB_FROM_QUERY: Record<string, ManageTab> = {
  tiers: "tiers",
  guests: "guests",
  requests: "requests",
  stripe: "stripe",
};

const tabFromSearch = (params: URLSearchParams): ManageTab => {
  const tab = params.get("tab");
  if (tab && TAB_FROM_QUERY[tab]) return TAB_FROM_QUERY[tab];
  if (params.get("stripe")) return "stripe";
  return "tiers";
};

const WeddingManage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [wedding, setWedding] = useState<Wedding | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState<ManageTab>(() => tabFromSearch(searchParams));
  const [pendingRequestCount, setPendingRequestCount] = useState(0);

  const setTab = (tab: ManageTab) => {
    setActiveTab(tab);
    const next = new URLSearchParams(searchParams);
    next.set("tab", tab);
    next.delete("stripe");
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    const nextTab = tabFromSearch(searchParams);
    setActiveTab(nextTab);
    if (searchParams.get("stripe") && id) {
      const next = new URLSearchParams(searchParams);
      next.delete("stripe");
      next.set("tab", "stripe");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, id]);

  const loadPendingCount = useCallback(async () => {
    if (!id || !user) return;
    try {
      const { data } = await getRequests(id);
      setPendingRequestCount(
        (data || []).filter((request) => request.status === "pending").length
      );
    } catch {
      setPendingRequestCount(0);
    }
  }, [id, user]);

  useEffect(() => {
    if (!id || authLoading) return;

    if (!user) {
      navigate("/login");
      return;
    }

    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await getWedding(id);
        if (data.created_by !== user.id) {
          setError("You do not have permission to manage this wedding.");
          setWedding(null);
          return;
        }
        setWedding(data);
        await loadPendingCount();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Wedding not found");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, user, authLoading, navigate, loadPendingCount]);

  useEffect(() => {
    if (activeTab === "requests") {
      loadPendingCount();
    }
  }, [activeTab, loadPendingCount]);

  if (authLoading || loading) return <Loading />;
  if (error) return <ErrorMessage message={error} />;
  if (!wedding || !id) return null;

  const publicSeatsEnabled = Boolean(wedding.allow_ghost_seats);
  const requestTabLabel =
    pendingRequestCount > 0
      ? `Public Seat Requests (${pendingRequestCount})`
      : "Public Seat Requests";

  return (
    <div className="page wedding-manage">
      <Link to="/my-weddings" className="back-link">
        &larr; Back to My Weddings
      </Link>

      <div className="wedding-header wedding-header-compact">
        <h1>{wedding.title}</h1>
        <p className="wedding-date">{formatDate(wedding.date)}</p>
        {wedding.location && <p className="wedding-location">{wedding.location}</p>}
        <div className="button-row" style={{ marginTop: "1rem" }}>
          <Link to={`/weddings/${id}/edit`} className="btn btn-secondary btn-sm">
            Edit Listing
          </Link>
          <Link to={`/weddings/${id}`} className="btn btn-secondary btn-sm">
            View Public Page
          </Link>
        </div>
      </div>

      <div className="tabs tabs-compact" role="tablist" aria-label="Wedding manage sections">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "tiers"}
          className={activeTab === "tiers" ? "tab active" : "tab"}
          onClick={() => setTab("tiers")}
        >
          Ticket Tiers
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "guests"}
          className={activeTab === "guests" ? "tab active" : "tab"}
          onClick={() => setTab("guests")}
        >
          Invite List
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "requests"}
          className={activeTab === "requests" ? "tab active" : "tab"}
          onClick={() => setTab("requests")}
        >
          {requestTabLabel}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "stripe"}
          className={activeTab === "stripe" ? "tab active" : "tab"}
          onClick={() => setTab("stripe")}
        >
          Link Stripe Account
        </button>
      </div>

      <div className="tab-content">
        {activeTab === "tiers" && <TierSetup weddingId={id} />}
        {activeTab === "guests" && <GuestListManager weddingId={id} />}
        {activeTab === "requests" &&
          (publicSeatsEnabled ? (
            <RequestList weddingId={id} onPendingCountChange={setPendingRequestCount} />
          ) : (
            <div className="empty-state">
              <p>Public Seats are not enabled for this wedding, so there are no public seat requests yet.</p>
              <p className="form-hint">
                Turn on Public Seats in Edit Listing, or add a Public Seats (Approval Required)
                tier under Ticket Tiers.
              </p>
            </div>
          ))}
        {activeTab === "stripe" && (
          <StripeConnectPanel returnPath={`/weddings/${id}/manage?tab=stripe`} />
        )}
      </div>
    </div>
  );
};

export default WeddingManage;
